import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { buildDraftFinalHandoffPath } from '../../../utils/draftFinal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useOrganization } from '../../../contexts/OrganizationContext';
import MainSidebarLayout from '../../layout/MainSidebarLayout';
import { useLocalization, type Language } from '../../../config/localization';
import { initializeLanguage } from '../../ui/navigation/LanguageSwitcher';
import Icon, { ICONS } from '../../ui/icons';
import VirtualizedImageGrid from './VirtualizedImageGrid';
import GalleryDisplaySettingsPanel, { type GalleryDisplaySettings } from './GalleryDisplaySettings';
import CollectionSelectModal from '../../ui/overlays/modals/CollectionSelectModal';
import MediaPreviewModal from '../../ui/overlays/modals/MediaPreviewModal';
import { galleryImageToSlot } from './galleryUtils';
import { useImagePreloader } from './useImagePreloader';
import type { GenerationSlot } from '../../../contexts/WidgetContext';
import { postGenerationMark, parseGenerationId, createMarkSerializer, type AssetMark } from '../../../utils/markGeneration';
import { resolveRecreateRoute } from '../../../utils/recreateRoute';
import { buildDownloadFilename } from '../../../utils/downloadNaming';

// Collection interface for modal
interface Collection {
  id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_private: boolean;
  can_edit: boolean;
  total_images?: number;
}

export interface GalleryFilters {
  search?: string;
  collectionIds?: number[];
  userIds?: string[];
  workflowIds?: number[];
  dateFrom?: string;
  dateTo?: string;
  urlType?: 0 | 1 | 2 | 3;
  contentTypes?: string[];
  marks?: string[];
  showTrashed?: boolean;
  limit: number;
  offset: number;
}

export interface GalleryImage {
  id: number;
  user_id: string;
  organization_id: number;
  workflow_id: number;
  raw_input: string;
  raw_response_json: string;
  preview_urls?: string;
  original_urls?: string;
  external_urls?: string;
  contentType?: string;
  /** Client-side classification used by media pickers. */
  mediaType?: 'image' | 'video' | 'audio' | 'model';
  /** Client-side resolved asset URL (for example the GLB rather than its poster). */
  resolvedUrl?: string;
  text_content?: string;
  status: 'pending' | 'success' | 'failed';
  error_message?: string;
  generation_count: number;
  url_type: number;
  created_at: string;
  // Per-asset quality mark (like / dislike / approved); null/undefined = unmarked
  mark?: 'like' | 'dislike' | 'approved' | null;
  // Additional fields from joins
  user_email?: string;
  workflow_name?: string;
  collections?: string;
}

// LocalStorage helpers for gallery filters
const GALLERY_FILTERS_KEY = 'galleryFilters';
const GALLERY_DISPLAY_SETTINGS_KEY = 'galleryDisplaySettings';

const getStoredFilters = (): Partial<GalleryFilters> => {
  try {
    const stored = localStorage.getItem(GALLERY_FILTERS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure we don't restore limit and offset from localStorage
      delete parsed.limit;
      delete parsed.offset;
      
      // Clean up empty arrays - they should be undefined instead
      if (Array.isArray(parsed.workflowIds) && parsed.workflowIds.length === 0) {
        delete parsed.workflowIds;
      }
      if (Array.isArray(parsed.collectionIds) && parsed.collectionIds.length === 0) {
        delete parsed.collectionIds;
      }
      if (Array.isArray(parsed.userIds) && parsed.userIds.length === 0) {
        delete parsed.userIds;
      }
      if (Array.isArray(parsed.marks) && parsed.marks.length === 0) {
        delete parsed.marks;
      }

      return parsed;
    }
  } catch (error) {
    console.warn('Failed to parse stored gallery filters:', error);
  }
  return {};
};

const storeFilters = (filters: GalleryFilters) => {
  try {
    // Don't store limit and offset
    const { limit, offset, ...filtersToStore } = filters;
    
    // Clean up empty arrays before storing
    const cleanedFilters: any = {};
    for (const [key, value] of Object.entries(filtersToStore)) {
      if (Array.isArray(value) && value.length === 0) {
        // Skip empty arrays
        continue;
      }
      if (value !== undefined) {
        cleanedFilters[key] = value;
      }
    }
    
    localStorage.setItem(GALLERY_FILTERS_KEY, JSON.stringify(cleanedFilters));
  } catch (error) {
    console.warn('Failed to store gallery filters:', error);
  }
};

const getStoredDisplaySettings = (): GalleryDisplaySettings => {
  try {
    const stored = localStorage.getItem(GALLERY_DISPLAY_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to parse stored gallery display settings:', error);
  }
  // Default settings
  return {
    viewMode: 'grid',
    zoomLevel: 3
  };
};

const storeDisplaySettings = (settings: GalleryDisplaySettings) => {
  try {
    localStorage.setItem(GALLERY_DISPLAY_SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to store gallery display settings:', error);
  }
};

const GalleryContent: React.FC = () => {
  const location = useLocation();
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const {
    userAccess,
    currentOrganization,
    isLoading: isOrgLoading
  } = useOrganization();

  // Language state
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(initializeLanguage());
  const t = useLocalization(selectedLanguage);

  // Gallery state
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);
  const nextOffsetRef = useRef(0);
  const lastLoadTimeRef = useRef(0);
  const galleryScrollRef = useRef<HTMLDivElement>(null);

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isTrashAction, setIsTrashAction] = useState(false);
  const [showTrashConfirm, setShowTrashConfirm] = useState(false);

  // Enhanced selection state (shift-click and drag-select)
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const [dragSelectValue, setDragSelectValue] = useState(true); // true = add to selection, false = remove

  // Collection modal state
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isAddingToCollections, setIsAddingToCollections] = useState(false);

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number } | null>(null);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show toast notification
  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Clean up toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const navigate = useNavigate();

  // Single item trash confirmation state
  const [showSingleTrashConfirm, setShowSingleTrashConfirm] = useState(false);
  const [pendingSingleTrashId, setPendingSingleTrashId] = useState<number | null>(null);
  const [skipSingleTrashConfirm, setSkipSingleTrashConfirm] = useState(() => {
    return localStorage.getItem('gallery_skip_single_trash_confirm') === 'true';
  });

  // Filter state - initialize with stored filters
  const [filters, setFilters] = useState<GalleryFilters>(() => {
    const storedFilters = getStoredFilters();
    console.log('💾 Loading stored gallery filters:', storedFilters);
    console.log('🔍 Initial filters will be:', {
      limit: 100,
      offset: 0,
      ...storedFilters,
      workflowIdsInfo: {
        hasWorkflowIds: !!storedFilters.workflowIds,
        length: storedFilters.workflowIds?.length,
        value: storedFilters.workflowIds
      }
    });
    return {
      limit: 100,
      offset: 0,
      ...storedFilters
    };
  });
  // Ref to access filters in callbacks without adding to dependency arrays
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Display settings - initialize with stored settings
  const [displaySettings, setDisplaySettings] = useState<GalleryDisplaySettings>(getStoredDisplaySettings);
  const [showDisplaySettings, setShowDisplaySettings] = useState(false);


  // Fetch images from collections API
  const fetchImages = useCallback(async (newFilters: GalleryFilters, append = false) => {
    if (!currentOrganization?.id) return;

    console.log('🔍 fetchImages called with filters:', {
      ...newFilters,
      hasWorkflowIds: !!newFilters.workflowIds,
      workflowIdsLength: newFilters.workflowIds?.length,
      workflowIdsValue: newFilters.workflowIds
    });

    setLoading(true);
    loadingRef.current = true;
    setError(null);

    try {
      const token = await getToken({ template: 'withmail' });
      
      // Build query parameters
      const params = new URLSearchParams({
        organizationId: currentOrganization.id.toString(),
        limit: newFilters.limit.toString(),
        offset: newFilters.offset.toString()
      });

      if (newFilters.search) params.set('search', newFilters.search);
      if (newFilters.collectionIds && newFilters.collectionIds.length > 0) {
        newFilters.collectionIds.forEach(id => params.append('collectionId', id.toString()));
      }
      if (newFilters.userIds && newFilters.userIds.length > 0) {
        newFilters.userIds.forEach(id => params.append('userId', id));
      }
      if (newFilters.workflowIds && newFilters.workflowIds.length > 0) {
        console.log('🚨 Adding workflowIds to params:', newFilters.workflowIds);
        newFilters.workflowIds.forEach(id => params.append('workflowId', id.toString()));
      }
      if (newFilters.dateFrom) params.set('dateFrom', newFilters.dateFrom);
      if (newFilters.dateTo) params.set('dateTo', newFilters.dateTo);
      if (newFilters.urlType !== undefined) params.set('urlType', newFilters.urlType.toString());
      if (newFilters.contentTypes && newFilters.contentTypes.length > 0) {
        newFilters.contentTypes.forEach(type => params.append('contentType', type));
      }
      if (newFilters.marks && newFilters.marks.length > 0) {
        newFilters.marks.forEach(mark => params.append('mark', mark));
      }
      if (newFilters.showTrashed) {
        params.set('showTrashed', 'true');
      }

      console.log('📡 Making API request with URL:', `/api/organization/collections/gallery?${params}`);
      
      const response = await fetch(`/api/organization/collections/gallery?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const fetchedImages = data.images || [];

        console.log('✅ API response:', {
          totalCount: data.totalCount,
          imagesReceived: fetchedImages.length,
          hasMore: data.hasMore,
          firstImage: fetchedImages[0]
        });

        if (append) {
          nextOffsetRef.current = newFilters.offset + fetchedImages.length;
          setImages(prev => {
            const existingIds = new Set(prev.map(img => img.id));
            const unique = fetchedImages.filter((img: GalleryImage) => !existingIds.has(img.id));
            return [...prev, ...unique];
          });
        } else {
          nextOffsetRef.current = newFilters.offset + fetchedImages.length;
          setImages(fetchedImages);
        }

        setTotalCount(data.totalCount || 0);
        setHasMore(fetchedImages.length === newFilters.limit);
      } else {
        const errorData = await response.json();
        console.error('❌ API error response:', errorData);
        setError(errorData.error || t.galleryPage?.errorLoading || 'Failed to load images');
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setError(t.galleryPage?.errorLoading || 'Error fetching images');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [currentOrganization?.id, getToken, t.galleryPage?.errorLoading]);

  // Collection fetch handler - defined early for use in initial load
  const fetchCollections = useCallback(async () => {
    if (!currentOrganization?.id) return;

    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch(
        `/api/organization/collections?organizationId=${currentOrganization.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter to only show collections user can edit
        const editableCollections = (data.collections || []).filter((c: Collection) => c.can_edit);
        setCollections(editableCollections);
      }
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  }, [currentOrganization?.id, getToken]);

  // Initial load
  useEffect(() => {
    if (userAccess?.hasAccess && currentOrganization?.id) {
      fetchImages(filters);
      fetchCollections(); // Fetch collections for determining default status
    }
  }, [userAccess?.hasAccess, currentOrganization?.id, fetchImages, fetchCollections]);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: Partial<GalleryFilters> | GalleryFilters) => {
    console.log('🎯 handleFiltersChange called with:', {
      newFilters,
      workflowIds: newFilters.workflowIds,
      workflowIdsType: typeof newFilters.workflowIds,
      workflowIdsLength: Array.isArray(newFilters.workflowIds) ? newFilters.workflowIds.length : 'N/A'
    });
    
    // If we receive a complete filters object (has limit and offset), use it directly
    // Otherwise merge with existing filters
    const isCompleteFilters = 'limit' in newFilters && 'offset' in newFilters;
    const updatedFilters = isCompleteFilters 
      ? { ...newFilters as GalleryFilters, offset: 0 } // Reset offset when filters change
      : {
          ...filters,
          ...newFilters,
          offset: 0 // Reset offset when filters change
        };
    
    console.log('💾 Saving gallery filters:', updatedFilters);
    setFilters(updatedFilters);
    storeFilters(updatedFilters); // Save filters to localStorage
    setImages([]); // Clear old images before fetching with new filters
    nextOffsetRef.current = 0;
    // Clear selection so hidden items can't be targeted by bulk actions
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
    fetchImages(updatedFilters);
  }, [filters, fetchImages]);

  // Handle load more (with 500ms debounce to prevent double-triggers from race conditions)
  // Uses filtersRef to avoid including filters in the dependency array, which would
  // cause this callback to get a new identity on every load (since setFilters is called
  // here), cascading into VirtualizedImageGrid's useEffect and clearing the cooldown timer.
  const handleLoadMore = useCallback(() => {
    if (loadingRef.current || !hasMore) return;

    const now = Date.now();
    if (now - lastLoadTimeRef.current < 500) return;
    lastLoadTimeRef.current = now;

    const nextFilters = {
      ...filtersRef.current,
      offset: nextOffsetRef.current
    };
    setFilters(nextFilters);
    fetchImages(nextFilters, true);
  }, [hasMore, fetchImages]);

  // Handle display settings
  const handleDisplaySettingsChange = (newSettings: Partial<GalleryDisplaySettings>) => {
    const updatedSettings = { ...displaySettings, ...newSettings };
    setDisplaySettings(updatedSettings);
    storeDisplaySettings(updatedSettings); // Save display settings to localStorage
  };

  // Selection mode handlers
  const handleToggleSelection = useCallback((id: number, index: number, shiftKey: boolean) => {
    if (shiftKey && lastSelectedIndex !== null) {
      // Shift-click: select range between last selected and current
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeIds = images.slice(start, end + 1).map(img => img.id);

      setSelectedIds(prev => {
        const newSet = new Set(prev);
        rangeIds.forEach(rangeId => newSet.add(rangeId));
        return newSet;
      });
    } else {
      // Normal click: toggle selection
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
      setLastSelectedIndex(index);
    }
  }, [images, lastSelectedIndex]);

  // Drag selection handlers
  const handleDragSelectStart = useCallback((id: number, index: number) => {
    setIsDragSelecting(true);
    // Determine mode: if starting on selected item - will deselect, otherwise will select
    const isCurrentlySelected = selectedIds.has(id);
    setDragSelectValue(!isCurrentlySelected);

    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (isCurrentlySelected) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
    setLastSelectedIndex(index);
  }, [selectedIds]);

  const handleDragSelectEnter = useCallback((id: number) => {
    if (!isDragSelecting) return;

    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (dragSelectValue) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, [isDragSelecting, dragSelectValue]);

  // Global mouseup to end drag selection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragSelecting) {
        setIsDragSelecting(false);
      }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragSelecting]);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(images.map(img => img.id)));
  }, [images]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  }, []);

  const handleExitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  }, []);

  // Escape key handler for selection mode
  useEffect(() => {
    if (!selectionMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      // Don't interfere with lightbox — it handles its own Esc
      if (lightboxIndex !== null) return;

      // Don't disrupt active download — progress UI would disappear
      if (isDownloading) return;

      e.preventDefault();

      if (selectedIds.size > 0) {
        // Clear selection but stay in selection mode
        handleDeselectAll();
      } else {
        // No selection — exit selection mode entirely
        handleExitSelectionMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectionMode, lightboxIndex, selectedIds.size, isDownloading, handleDeselectAll, handleExitSelectionMode]);

  // Collection handlers (bulk add for multiple selected images)
  const handleAddToCollections = useCallback(async (collectionIds: number[], _initialIds: number[]) => {
    if (selectedIds.size === 0 || !currentOrganization?.id) return;

    setIsAddingToCollections(true);
    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch(
        `/api/organization/collections/generations?organizationId=${currentOrganization.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            generation_ids: Array.from(selectedIds),
            collection_ids: collectionIds
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Added ${result.added_count} items to collections`);
        setShowCollectionModal(false);
        handleExitSelectionMode();
      } else {
        const errorData = await response.json();
        console.error('Failed to add to collections:', errorData.error);
      }
    } catch (error) {
      console.error('Failed to add to collections:', error);
    } finally {
      setIsAddingToCollections(false);
    }
  }, [selectedIds, currentOrganization?.id, getToken, handleExitSelectionMode]);

  // Single item add to collections handler
  const handleSingleAddToCollections = useCallback(async (imageId: number, collectionIds: number[]) => {
    if (!currentOrganization?.id) return;

    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch(
        `/api/organization/collections/generations?organizationId=${currentOrganization.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            generation_id: imageId,
            collection_ids: collectionIds
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Added image ${imageId} to ${result.added_count} collections`);
        // Refresh images to update collections data
        fetchImages(filters);
      } else {
        const errorData = await response.json();
        console.error('Failed to add to collections:', errorData.error);
      }
    } catch (error) {
      console.error('Failed to add to collections:', error);
    }
  }, [currentOrganization?.id, getToken, filters, fetchImages]);

  // Single item remove from collection handler
  const handleSingleRemoveFromCollection = useCallback(async (imageId: number, collectionId: number) => {
    if (!currentOrganization?.id) return;

    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch(
        `/api/organization/collections/generations?organizationId=${currentOrganization.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            generation_id: imageId,
            collection_id: collectionId
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Removed image ${imageId} from collection ${collectionId}`);
        // Refresh images to update collections data
        fetchImages(filters);
      } else {
        const errorData = await response.json();
        console.error('Failed to remove from collection:', errorData.error);
      }
    } catch (error) {
      console.error('Failed to remove from collection:', error);
    }
  }, [currentOrganization?.id, getToken, filters, fetchImages]);

  // Trash/Restore handlers
  const handleMoveToTrash = useCallback(async () => {
    if (selectedIds.size === 0 || !currentOrganization?.id) return;

    setIsTrashAction(true);
    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch('/api/organization/generations/trash', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          generation_ids: Array.from(selectedIds),
          organization_id: currentOrganization.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`🗑️ Moved ${data.trashedCount} items to trash`);
        // Refresh the gallery from the beginning
        handleExitSelectionMode();
        nextOffsetRef.current = 0;
        fetchImages({ ...filters, offset: 0 });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to move to trash');
      }
    } catch (error) {
      console.error('Error moving to trash:', error);
      setError('Error moving to trash');
    } finally {
      setIsTrashAction(false);
      setShowTrashConfirm(false);
    }
  }, [selectedIds, currentOrganization?.id, getToken, filters, fetchImages, handleExitSelectionMode]);

  const handleRestoreFromTrash = useCallback(async () => {
    if (selectedIds.size === 0 || !currentOrganization?.id) return;

    setIsTrashAction(true);
    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch('/api/organization/generations/restore', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          generation_ids: Array.from(selectedIds),
          organization_id: currentOrganization.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`♻️ Restored ${data.restoredCount} items from trash`);
        // Refresh the gallery from the beginning
        handleExitSelectionMode();
        nextOffsetRef.current = 0;
        fetchImages({ ...filters, offset: 0 });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to restore');
      }
    } catch (error) {
      console.error('Error restoring from trash:', error);
      setError('Error restoring from trash');
    } finally {
      setIsTrashAction(false);
    }
  }, [selectedIds, currentOrganization?.id, getToken, filters, fetchImages, handleExitSelectionMode]);

  // Execute single item trash (after confirmation or if skipped)
  // Returns true on success, false on failure
  const executeSingleItemTrash = useCallback(async (generationId: number): Promise<boolean> => {
    if (!currentOrganization?.id) return false;

    setIsTrashAction(true);
    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch('/api/organization/generations/trash', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          generation_ids: [generationId],
          organization_id: currentOrganization.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        const actuallyTrashed = data.trashedCount ?? 1;
        if (actuallyTrashed > 0) {
          console.log(`🗑️ Moved item ${generationId} to trash`);
          // Remove from current images list for immediate feedback
          setImages(prev => prev.filter(img => img.id !== generationId));
          setTotalCount(prev => prev - 1);
          // Decrement pagination offset so next load-more doesn't skip a row
          nextOffsetRef.current = Math.max(0, nextOffsetRef.current - 1);
        }
        return actuallyTrashed > 0;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to move to trash');
        return false;
      }
    } catch (error) {
      console.error('Error moving to trash:', error);
      setError('Error moving to trash');
      return false;
    } finally {
      setIsTrashAction(false);
      setShowSingleTrashConfirm(false);
      setPendingSingleTrashId(null);
    }
  }, [currentOrganization?.id, getToken]);

  // Single item trash handler (for trash icon on each card)
  const handleSingleItemTrash = useCallback((generationId: number): Promise<boolean> | void => {
    if (!currentOrganization?.id) return;

    // Check if user wants to skip confirmation
    if (skipSingleTrashConfirm) {
      return executeSingleItemTrash(generationId);
    } else {
      // Show confirmation dialog
      setPendingSingleTrashId(generationId);
      setShowSingleTrashConfirm(true);
    }
  }, [currentOrganization?.id, skipSingleTrashConfirm, executeSingleItemTrash]);

  // Helper to parse URLs from JSON string
  const parseUrls = (urlField: string | undefined | null): string[] => {
    if (!urlField) return [];
    try {
      const parsed = JSON.parse(urlField);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [urlField];
    }
  };

  // Download selected images handler - bundles into a ZIP file
  const handleDownloadSelected = useCallback(async () => {
    if (selectedIds.size === 0 || isDownloading || !currentOrganization?.id) return;

    setIsDownloading(true);
    const selectedImages = images.filter(img => selectedIds.has(img.id));

    // Count total files to download (across all layers)
    let totalFiles = 0;
    const imageLayerCounts: number[] = [];
    for (const image of selectedImages) {
      const originalUrls = parseUrls(image.original_urls);
      const previewUrls = parseUrls(image.preview_urls);
      const externalUrls = parseUrls(image.external_urls);
      const layers = Math.max(originalUrls.length, previewUrls.length, externalUrls.length, 1);
      imageLayerCounts.push(layers);
      totalFiles += layers;
    }

    setDownloadProgress({ current: 0, total: totalFiles });

    try {
      const token = await getToken({ template: 'withmail' });
      const zip = new JSZip();
      const usedFilenames = new Set<string>();
      let downloaded = 0;
      let failed = 0;

      for (let i = 0; i < selectedImages.length; i++) {
        const image = selectedImages[i];
        const totalLayers = imageLayerCounts[i];

        for (let index = 0; index < totalLayers; index++) {
          try {
            const downloadUrl = `/api/organization/generations/download?id=${image.id}&organizationId=${currentOrganization.id}&index=${index}`;

            const response = await fetch(downloadUrl, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
              const blob = await response.blob();
              const contentDisposition = response.headers.get('content-disposition');

              // Extract filename from Content-Disposition header or generate one.
              // The fallback goes through the shared naming helper so a model
              // does not land in the ZIP as `.jpg` when the header is missing.
              // Layer priority must match the endpoint's (original > preview >
              // external, `download.ts`), or the fallback guesses the extension
              // from a different file than the one actually returned.
              const layerUrl =
                parseUrls(image.original_urls)[index] ||
                parseUrls(image.preview_urls)[index] ||
                parseUrls(image.external_urls)[index];
              let filename = buildDownloadFilename(
                image.contentType,
                image.id,
                layerUrl,
                totalLayers > 1 ? index + 1 : undefined
              );
              if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+?)"/);
                if (match) filename = match[1];
              }

              // Deduplicate filenames to prevent silent overwrites
              let uniqueFilename = filename;
              let counter = 1;
              while (usedFilenames.has(uniqueFilename)) {
                const dotIdx = filename.lastIndexOf('.');
                uniqueFilename = dotIdx >= 0
                  ? `${filename.slice(0, dotIdx)}_${counter}${filename.slice(dotIdx)}`
                  : `${filename}_${counter}`;
                counter++;
              }
              usedFilenames.add(uniqueFilename);

              zip.file(uniqueFilename, blob);
            } else {
              console.error(`Failed to download image ${image.id} layer ${index}:`, response.status);
              failed++;
            }
          } catch (error) {
            console.error(`Failed to download image ${image.id} layer ${index}:`, error);
            failed++;
          }

          downloaded++;
          setDownloadProgress({ current: downloaded, total: totalFiles });
        }
      }

      // Generate and save ZIP if we got any files
      if (Object.keys(zip.files).length > 0) {
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, 'gallery-images.zip');
        if (failed > 0) {
          showToast(
            (t.galleryPage?.downloadPartialFail || '{count} file(s) failed to download')
              .replace('{count}', failed.toString()),
            'error'
          );
        }
      } else {
        showToast(t.galleryPage?.downloadFailed || 'All downloads failed', 'error');
      }
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  }, [selectedIds, images, isDownloading, currentOrganization?.id, getToken, showToast, t.galleryPage?.downloadFailed, t.galleryPage?.downloadPartialFail]);

  // Single item restore handler (for restore icon on each card in trash mode)
  const handleSingleItemRestore = useCallback(async (generationId: number) => {
    if (!currentOrganization?.id) return;

    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch('/api/organization/generations/restore', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          generation_ids: [generationId],
          organization_id: currentOrganization.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        const actuallyRestored = data.restoredCount ?? 1;
        if (actuallyRestored > 0) {
          console.log(`♻️ Restored item ${generationId} from trash`);
          // Remove from current images list for immediate feedback
          setImages(prev => prev.filter(img => img.id !== generationId));
          setTotalCount(prev => prev - 1);
          // Decrement pagination offset so next load-more doesn't skip a row
          nextOffsetRef.current = Math.max(0, nextOffsetRef.current - 1);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to restore');
      }
    } catch (error) {
      console.error('Error restoring from trash:', error);
      setError('Error restoring from trash');
    }
  }, [currentOrganization?.id, getToken]);

  // Per-generation request sequence so out-of-order mark failures only roll back
  // when they are still the latest in-flight request for that id. A plain
  // value-equality guard has an ABA race (like→dislike→like): an early failing
  // request whose optimistic value matches a *later* succeeded request would
  // wrongly roll the UI back. The sequence makes "is this still the newest
  // request?" the rollback condition instead of "does the value still match?".
  const markSeqRef = useRef<Map<number, number>>(new Map());

  // Per-asset write queue: serializes the POSTs for one generation so the server
  // applies them in click order (handles out-of-order *successes*, not just the
  // failure ABA above). See createMarkSerializer.
  const markQueueRef = useRef(createMarkSerializer());

  // Last server-confirmed mark per generation. Rollback restores THIS, not the
  // optimistic predecessor: if `like`→`dislike` both POSTs fail, the predecessor
  // (`like`) never persisted either, so rolling back to it would leave the UI on
  // `like` while the server is still on the pre-burst value. Initialized lazily
  // from the pre-optimistic value on first touch, advanced on each success, and
  // dropped once the asset's latest write settles (UI === server again).
  const confirmedMarkRef = useRef<Map<number, AssetMark | null>>(new Map());

  // Set/unset a per-asset mark (like / dislike / approved) with optimistic update
  // + rollback. Accepts a string (slot.id from the modal) or numeric image id.
  const handleMark = useCallback(async (id: string | number, mark: AssetMark | null) => {
    const generationId = parseGenerationId(id);
    if (generationId === null || !currentOrganization?.id) return;

    const seq = (markSeqRef.current.get(generationId) ?? 0) + 1;
    markSeqRef.current.set(generationId, seq);

    // Optimistically apply the new mark. Record the pre-burst server value the
    // first time this asset is touched (no in-flight write yet → UI === server).
    setImages(prev => prev.map(img => {
      if (img.id === generationId) {
        if (!confirmedMarkRef.current.has(generationId)) {
          confirmedMarkRef.current.set(generationId, img.mark ?? null);
        }
        return { ...img, mark };
      }
      return img;
    }));

    try {
      await markQueueRef.current(generationId, async () => {
        const token = await getToken({ template: 'withmail' });
        const ok = await postGenerationMark({
          token,
          generationId,
          organizationId: currentOrganization.id,
          mark,
        });
        if (!ok) throw new Error('mark request failed');
        // Persisted — this is now the server-confirmed value.
        confirmedMarkRef.current.set(generationId, mark);
      });
    } catch (error) {
      console.error('Error setting mark:', error);
      // Roll back only if this is still the latest mark request for this
      // generation (avoid clobbering a succeeded later mark when an earlier
      // request fails out of order, including the ABA case). Restore the last
      // server-confirmed value — the optimistic predecessor may itself have
      // failed to persist.
      if (markSeqRef.current.get(generationId) === seq) {
        const confirmed = confirmedMarkRef.current.get(generationId) ?? null;
        setImages(prev => prev.map(img =>
          img.id === generationId ? { ...img, mark: confirmed } : img
        ));
      }
      showToast(t.gallery?.markFailed || 'Failed to update mark', 'error');
    } finally {
      // Once the asset's newest write has settled, UI === server again, so the
      // confirmed snapshot is no longer needed (keeps the map from growing).
      if (markSeqRef.current.get(generationId) === seq) {
        confirmedMarkRef.current.delete(generationId);
      }
    }
  }, [currentOrganization?.id, getToken, showToast, t.gallery?.markFailed]);

  // Convert gallery images to GenerationSlots for the lightbox.
  // Maintain a mapping from slot index to image index since some images may be
  // filtered out (e.g., success status but no URLs), causing array lengths to differ.
  const { allSlots, slotToImageIndex } = useMemo(() => {
    const slots: GenerationSlot[] = [];
    const indexMap: number[] = [];
    images.forEach((img, i) => {
      const slot = galleryImageToSlot(img);
      if (slot) {
        slots.push(slot);
        indexMap.push(i);
      }
    });
    return { allSlots: slots, slotToImageIndex: indexMap };
  }, [images]);

  // Clamp lightbox index when allSlots shrinks (e.g., after confirmed trash).
  // Without this, lightboxIndex can point past the end of allSlots, leaving
  // the modal in a stale "open but no slot" state.
  useEffect(() => {
    if (lightboxIndex === null) return;
    if (allSlots.length === 0) {
      setLightboxIndex(null);
    } else if (lightboxIndex >= allSlots.length) {
      setLightboxIndex(allSlots.length - 1);
    }
  }, [allSlots.length, lightboxIndex]);

  // Preload adjacent images for instant lightbox navigation
  useImagePreloader(allSlots, lightboxIndex);

  // Lightbox handlers
  const handleImageClick = useCallback((index: number) => {
    // Convert images-array index to slot index
    const slotIdx = slotToImageIndex.indexOf(index);
    if (slotIdx !== -1) {
      setLightboxIndex(slotIdx);
    }
  }, [slotToImageIndex]);

  const handleSlotNavigate = useCallback((newIndex: number) => {
    if (newIndex >= 0 && newIndex < allSlots.length) {
      setLightboxIndex(newIndex);
      // Auto-load more images when navigating near the end
      if (newIndex >= allSlots.length - 3 && hasMore && !loadingRef.current) {
        handleLoadMore();
      }
    }
  }, [allSlots.length, hasMore, handleLoadMore]);

  const handleLightboxClose = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  // Lightbox trash handler — trashes the image and advances to next or closes.
  // When confirmation is enabled (skipSingleTrashConfirm === false), we must NOT
  // adjust the lightbox index immediately because the user may cancel the modal.
  // Instead, only advance when the trash is executed without confirmation.
  const handleLightboxTrash = useCallback(async (id: number) => {
    if (skipSingleTrashConfirm) {
      // Trash will execute immediately — await result before adjusting lightbox
      const success = await handleSingleItemTrash(id);
      if (success && lightboxIndex !== null) {
        if (allSlots.length <= 1) {
          setLightboxIndex(null);
        } else if (lightboxIndex >= allSlots.length - 1) {
          setLightboxIndex(lightboxIndex - 1);
        }
        // Otherwise stay at same index (next image slides in)
      }
    } else {
      // Confirmation modal will appear — don't adjust lightbox yet.
      // The lightbox will update reactively when the image is removed from the list
      // after the user confirms (executeSingleItemTrash removes from images array,
      // which updates allSlots via useMemo, naturally adjusting the lightbox).
      handleSingleItemTrash(id);
    }
  }, [handleSingleItemTrash, lightboxIndex, allSlots.length, skipSingleTrashConfirm]);

  // Lightbox recreate handler — replicates GalleryImageCard.handleRecreate logic
  // Drafts are finalized in the workbench, which owns dispatch and polling.
  // The URL carries only IDs; the workbench reloads the draft and confirms.
  const handleLightboxDraftFinal = useCallback((slot: GenerationSlot) => {
    const generationId = Number(slot.id);
    if (!slot.workflowId || !Number.isSafeInteger(generationId)) return;
    navigate(buildDraftFinalHandoffPath(slot.contentType, slot.workflowId, generationId));
  }, [navigate]);

  const handleLightboxRecreate = useCallback(async () => {
    if (lightboxIndex === null || !currentOrganization?.id) return;
    const imageIdx = slotToImageIndex[lightboxIndex];
    const image = imageIdx !== undefined ? images[imageIdx] : undefined;
    if (!image) return;

    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch(`/api/user/workflows/${image.workflow_id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Organization-Id': currentOrganization.id.toString()
        }
      });

      if (!response.ok) {
        showToast(t.gallery?.workflowUnavailable || 'Workflow is unavailable or has been deleted', 'error');
        return;
      }

      const workflowDetails = await response.json();

      let rawInput: Record<string, unknown> = {};
      try {
        rawInput = JSON.parse(image.raw_input || '{}');
      } catch {
        // ignore
      }

      let mediaUrls: string[] = [];
      try {
        if (rawInput.image_urls && Array.isArray(rawInput.image_urls)) {
          mediaUrls = rawInput.image_urls;
        } else if (rawInput.video_urls && Array.isArray(rawInput.video_urls)) {
          mediaUrls = rawInput.video_urls;
        } else if (rawInput.audio_urls && Array.isArray(rawInput.audio_urls)) {
          mediaUrls = rawInput.audio_urls;
        }
      } catch {
        // ignore
      }

      const standardFields = ['prompt', 'text', 'num_images', 'gen_count',
        'guidance_scale', 'safety_tolerance', 'num_inference_steps'];

      for (const [fieldId, value] of Object.entries(rawInput)) {
        if (!standardFields.includes(fieldId) && value !== undefined && value !== null) {
          try {
            const storageKey = `embedded_widget_${image.workflow_id}_${fieldId}_${currentOrganization.id}`;
            localStorage.setItem(storageKey, JSON.stringify(value));
          } catch {
            // ignore
          }
        }
      }

      sessionStorage.setItem('recreate-settings', JSON.stringify({
        workflowId: image.workflow_id,
        rawInput,
        mediaUrls,
        // Stamped for the same reason as `recreate-prompt-v2` below — see
        // `src/utils/recreateStorage.ts`.
        organizationId: currentOrganization.id,
        timestamp: Date.now()
      }));

      sessionStorage.setItem('recreate-dynamic-values', JSON.stringify({
        rawInput,
        organizationId: currentOrganization.id,
        timestamp: Date.now()
      }));

      // Always clear first to prevent stale data from a previous recreate
      sessionStorage.removeItem('recreate-prompt-v2');
      const promptV2Data = rawInput.prompt_v2 || (rawInput.inputs as Record<string, unknown>)?.prompt_v2;
      if (promptV2Data) {
        sessionStorage.setItem('recreate-prompt-v2', JSON.stringify({
          promptV2: promptV2Data,
          workflowId: image.workflow_id,
          // The blob carries this organization's reference bodies, prompt ids and
          // media URLs. PromptEditorV2Widget rejects it unless the org still
          // matches, so a switch between Recreate and the editor loading can't
          // seed a generation billed to another org with this org's references.
          organizationId: currentOrganization.id,
          timestamp: Date.now()
        }));
      }

      // Always clear first to prevent stale data from a previous recreate
      sessionStorage.removeItem('recreate-media-urls');
      if (mediaUrls.length > 0) {
        // These URLs become the input media of a BILLED generation, so the blob
        // is org-stamped like the others — the media widgets reject a foreign
        // one instead of feeding another tenant's assets into this generation.
        // See `src/utils/recreateStorage.ts`.
        sessionStorage.setItem('recreate-media-urls', JSON.stringify({
          mediaUrls,
          organizationId: currentOrganization.id,
          timestamp: Date.now()
        }));
      }

      // Store per-slot data for multi-slot workflows
      // Always clear first to prevent stale data from a previous recreate
      sessionStorage.removeItem('recreate-slots-data');
      try {
        const slotsWidget = (workflowDetails.widgets || []).find(
          (w: { widget_type_id: number }) => w.widget_type_id === 10
        );
        if (slotsWidget?.config_data) {
          const slotsConfig = typeof slotsWidget.config_data === 'string'
            ? JSON.parse(slotsWidget.config_data)
            : slotsWidget.config_data;
          const slots = slotsConfig?.slots;
          if (Array.isArray(slots) && slots.length > 0) {
            const slotMappings: { slotId: string; urls: string[] }[] = [];
            for (const slot of slots) {
              const slotValue = rawInput[slot.slot_id] ?? (rawInput.inputs as Record<string, unknown>)?.[slot.slot_id];
              if (slotValue) {
                const urls = Array.isArray(slotValue) ? slotValue : [slotValue];
                slotMappings.push({ slotId: slot.slot_id, urls: urls.filter((u: unknown) => typeof u === 'string') });
              }
            }
            if (slotMappings.length > 0) {
              // Same org data as `recreate-media-urls` above, just keyed per
              // slot — stamped for the same reason.
              sessionStorage.setItem('recreate-slots-data', JSON.stringify({
                slotMappings,
                organizationId: currentOrganization.id,
                timestamp: Date.now()
              }));
            }
          }
        }
      } catch (e) {
        console.warn('Failed to extract per-slot data for recreate:', e);
      }

      // Store collection IDs for pre-selection after recreate
      // Always clear first to prevent stale data from a previous recreate on parse failure
      sessionStorage.removeItem('recreate-collections');
      try {
        const collectionsJson = image.collections || '[]';
        const collections: { id: number; name: string; is_default: boolean }[] = JSON.parse(collectionsJson);
        const collectionIds = collections.map(c => c.id).filter(id => typeof id === 'number');
        // Always store — empty array signals "no collections" to clear existing selections
        // Collection ids live in a per-organization id space, so the blob is
        // org-stamped like the others (see `src/utils/recreateStorage.ts`).
        sessionStorage.setItem('recreate-collections', JSON.stringify({
          collectionIds,
          organizationId: currentOrganization.id,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to extract collections for recreate:', e);
      }

      const route = resolveRecreateRoute(workflowDetails.workflow?.category);

      setLightboxIndex(null);
      navigate(`${route}?workflow=${image.workflow_id}&recreate=true`);
    } catch (error) {
      console.error('Failed to recreate:', error);
      showToast(t.gallery?.workflowUnavailable || 'Failed to recreate generation', 'error');
    }
  }, [lightboxIndex, images, slotToImageIndex, currentOrganization?.id, getToken, navigate, showToast, t.gallery?.workflowUnavailable]);

  // Lightbox collection handlers
  const handleLightboxAddToCollections = useCallback(async (imageId: number, collectionIds: number[]) => {
    await handleSingleAddToCollections(imageId, collectionIds);
  }, [handleSingleAddToCollections]);

  const handleLightboxRemoveFromCollection = useCallback(async (imageId: number, collectionId: number) => {
    await handleSingleRemoveFromCollection(imageId, collectionId);
  }, [handleSingleRemoveFromCollection]);

  // Authentication checks
  if (!user) {
    return (
      <MainSidebarLayout
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
        loading={false}
      >
        <div className="text-center py-16">
          <Icon name={ICONS.LOCK} size={96} className="mx-auto mb-6 text-gray-500" />
          <h2 className="text-3xl font-bold mb-4">{t.auth?.required || 'Authentication Required'}</h2>
          <p className="text-xl text-purple-200">
            {t.auth?.loginRequired || 'Please log in to access the gallery'}
          </p>
        </div>
      </MainSidebarLayout>
    );
  }

  if (!userAccess?.hasAccess && userAccess) {
    return (
      <MainSidebarLayout
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
        loading={false}
      >
        <div className="text-center py-16">
          <Icon name={ICONS.WARNING} size={96} className="mx-auto mb-6 text-yellow-500" />
          <h2 className="text-3xl font-bold mb-4">{t.galleryPage?.noAccess || 'No Access'}</h2>
          <p className="text-xl text-purple-200 mb-4">
            {t.galleryPage?.noAccessDesc || 'You do not have access to the image gallery'}
          </p>
        </div>
      </MainSidebarLayout>
    );
  }

  if (!currentOrganization?.id) {
    return (
      <MainSidebarLayout
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
        loading={false}
      >
        <div className="text-center py-16">
          <Icon name={ICONS.BUILDING} size={96} className="mx-auto mb-6 text-gray-500" />
          <h2 className="text-3xl font-bold mb-4">{t.common?.selectOrganization || 'Select Organization'}</h2>
          <p className="text-xl text-purple-200">
            {t.common?.selectOrganizationDesc || 'Please select an organization to view the gallery'}
          </p>
        </div>
      </MainSidebarLayout>
    );
  }

  return (
    <MainSidebarLayout
      selectedLanguage={selectedLanguage}
      onLanguageChange={setSelectedLanguage}
      galleryFilters={filters}
      onGalleryFiltersChange={handleFiltersChange}
    >
      {/* Main content container with BigWidgetWrapper styling and proper margins.
           h-[calc(100vh-24px)] gives a definite height so the inner overflow-y-auto
           scroll container works correctly (the parent <main> only has min-h-screen,
           which doesn't bound flex children). */}
      <div className="relative h-[calc(100vh-24px)] flex flex-col overflow-hidden bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-3xl border-2 border-purple-500/50 shadow-2xl backdrop-blur-md hover:border-purple-400/70 transition-all duration-300 hover:shadow-purple-500/20 m-3">
        {/* Header - stays fixed at top, outside scrollable area */}
        <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-gray-700/40 bg-gradient-to-r from-purple-900/20 to-blue-900/20">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/30 to-blue-500/30 flex items-center justify-center border border-purple-400/40 shadow-lg">
              <Icon name={filters.showTrashed ? ICONS.TRASH : ICONS.GALLERY} size={18} className="text-white filter drop-shadow-sm" />
            </div>
            <h2 className="text-lg font-bold text-white bg-gradient-to-r from-purple-200 to-blue-200 bg-clip-text text-transparent">
              {filters.showTrashed ? (t.gallery?.trash || 'Trash') : (t.galleryPage?.title || 'Gallery')}
            </h2>
            {/* Trash mode info */}
            {filters.showTrashed && (
              <span className="text-xs text-gray-400">
                {t.gallery?.trashAutoHide || 'Items are hidden after 30 days'}
              </span>
            )}
            {/* Show active filters count */}
            {(filters.workflowIds?.length || filters.collectionIds?.length || filters.userIds?.length || filters.contentTypes?.length || filters.dateFrom || filters.dateTo) && (
              <div className="flex items-center gap-2 ml-4">
                <span className="text-xs text-purple-300">
                  {t.galleryPage?.activeFilters || 'Active filters:'}
                </span>
                <span className="px-2 py-0.5 bg-purple-600/40 rounded-full text-xs text-purple-200">
                  {(filters.workflowIds?.length || 0) + (filters.collectionIds?.length || 0) + (filters.userIds?.length || 0) + (filters.contentTypes?.length || 0) + (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Selection mode toolbar */}
            {selectionMode ? (
              <>
                {/* Selection count */}
                <span className="text-sm text-purple-300">
                  {t.gallery?.selected || 'Selected'}: {selectedIds.size}
                </span>

                {/* Select all / Deselect all */}
                <button
                  onClick={selectedIds.size === images.length ? handleDeselectAll : handleSelectAll}
                  className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-purple-600/40 hover:bg-purple-500/50 border border-purple-400/40 hover:border-purple-300/60 text-white transition-all duration-300 text-sm"
                >
                  {selectedIds.size === images.length
                    ? (t.gallery?.deselectAll || 'Deselect All')
                    : (t.gallery?.selectAll || 'Select All')
                  }
                </button>

                {/* Action buttons based on trash mode */}
                {selectedIds.size > 0 && (
                  filters.showTrashed ? (
                    <button
                      onClick={handleRestoreFromTrash}
                      disabled={isTrashAction}
                      className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-green-600/40 hover:bg-green-500/50 border border-green-400/40 hover:border-green-300/60 text-white transition-all duration-300 text-sm disabled:opacity-50"
                    >
                      <Icon name={ICONS.REFRESH} size={14} />
                      <span>{t.gallery?.restore || 'Restore'}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowTrashConfirm(true)}
                      disabled={isTrashAction}
                      className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-red-600/40 hover:bg-red-500/50 border border-red-400/40 hover:border-red-300/60 text-white transition-all duration-300 text-sm disabled:opacity-50"
                    >
                      <Icon name={ICONS.TRASH} size={14} />
                      <span>{t.gallery?.moveToTrash || 'Move to Trash'}</span>
                    </button>
                  )
                )}

                {/* Add to Collection button - only show when not in trash mode */}
                {selectedIds.size > 0 && !filters.showTrashed && (
                  <button
                    onClick={() => {
                      fetchCollections();
                      setShowCollectionModal(true);
                    }}
                    disabled={isAddingToCollections}
                    className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-blue-600/40 hover:bg-blue-500/50 border border-blue-400/40 hover:border-blue-300/60 text-white transition-all duration-300 text-sm disabled:opacity-50"
                  >
                    <Icon name={ICONS.FOLDER} size={14} />
                    <span>{t.gallery?.addToCollection || 'Add to Collection'}</span>
                  </button>
                )}

                {/* Download selected button */}
                {selectedIds.size > 0 && (
                  <button
                    onClick={handleDownloadSelected}
                    disabled={isDownloading}
                    className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-green-600/40 hover:bg-green-500/50 border border-green-400/40 hover:border-green-300/60 text-white transition-all duration-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon name={ICONS.DOWNLOAD} size={14} className={isDownloading ? 'animate-bounce' : ''} />
                    <span>{isDownloading && downloadProgress
                      ? `${downloadProgress.current}/${downloadProgress.total}...`
                      : isDownloading
                        ? (t.common?.processing || 'Downloading...')
                        : (t.galleryPage?.downloadSelected || 'Download Selected')
                    }</span>
                  </button>
                )}

                {/* Cancel selection */}
                <button
                  onClick={handleExitSelectionMode}
                  className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-gray-600/40 hover:bg-gray-500/50 border border-gray-400/40 hover:border-gray-300/60 text-white transition-all duration-300 text-sm"
                >
                  <Icon name={ICONS.X} size={14} />
                  <span>{t.common?.cancel || 'Cancel'}</span>
                </button>
              </>
            ) : (
              <>
                {/* Clear filters button - only show if filters are active */}
                {(filters.workflowIds?.length || filters.collectionIds?.length || filters.userIds?.length || filters.contentTypes?.length || filters.dateFrom || filters.dateTo) && (
                  <button
                    onClick={() => {
                      const clearedFilters: GalleryFilters = {
                        limit: 100,
                        offset: 0,
                        showTrashed: filters.showTrashed
                      };
                      console.log('🧹 Clearing all filters');
                      setFilters(clearedFilters);
                      storeFilters(clearedFilters);
                      nextOffsetRef.current = 0;
                      fetchImages(clearedFilters);
                      // Also clear localStorage to prevent old filters from persisting
                      localStorage.removeItem(GALLERY_FILTERS_KEY);
                    }}
                    className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-red-600/40 hover:bg-red-500/50 border border-red-400/40 hover:border-red-300/60 text-white hover:text-red-100 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl text-sm"
                    title={t.galleryPage?.clearAllFilters || 'Clear all filters'}
                  >
                    <Icon name={ICONS.X} size={14} />
                    <span>{t.galleryPage?.clearFilters || 'Clear'}</span>
                  </button>
                )}

                {/* Select mode toggle */}
                <button
                  onClick={() => setSelectionMode(true)}
                  className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-purple-600/40 hover:bg-purple-500/50 border border-purple-400/40 hover:border-purple-300/60 text-white transition-all duration-300 text-sm"
                  title={t.gallery?.selectMode || 'Select items'}
                >
                  <Icon name={ICONS.CHECK} size={14} />
                  <span>{t.gallery?.select || 'Select'}</span>
                </button>

                <button
                  onClick={() => setShowDisplaySettings(!showDisplaySettings)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-r from-purple-600/40 to-blue-600/40 hover:from-purple-500/50 hover:to-blue-500/50 border border-purple-400/40 hover:border-purple-300/60 text-white hover:text-purple-200 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                  title={t.gallery?.displayOptions || 'Display Options'}
                >
                  <Icon name={ICONS.SLIDERS} size={16} />
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Subtle border glow effect */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none -z-10 blur-xl"></div>

        {/* Content area - scrollable container below fixed header */}
        <div ref={galleryScrollRef} className="flex-1 min-h-0 overflow-y-auto flex flex-col">
          <div className="p-0 flex-1 min-h-0 flex flex-col">
            {/* Error State */}
            {error && (
              <div className="mb-4">
                <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Icon name={ICONS.WARNING} size={20} className="text-red-400" />
                    <div>
                      <h3 className="text-red-400 font-medium">{t.galleryPage?.errorLoading || 'Error Loading'}</h3>
                      <p className="text-red-300 text-sm mt-1">{error}</p>
                    </div>
                    <button
                      onClick={() => fetchImages(filters)}
                      className="ml-auto px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                    >
                      {t.galleryPage?.retry || 'Retry'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Gallery Grid - Main content */}
            <div className="flex-1 min-h-0">
              <VirtualizedImageGrid
                images={images}
                loading={loading}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                viewMode={displaySettings.viewMode}
                zoomLevel={displaySettings.zoomLevel}
                language={selectedLanguage}
                organizationId={currentOrganization.id}
                selectionMode={selectionMode}
                selectedIds={selectedIds}
                onToggleSelection={handleToggleSelection}
                onDragSelectStart={handleDragSelectStart}
                onDragSelectEnter={handleDragSelectEnter}
                isDragSelecting={isDragSelecting}
                onMoveToTrash={handleSingleItemTrash}
                onMark={handleMark}
                onRestore={handleSingleItemRestore}
                isTrashMode={filters.showTrashed}
                collections={collections}
                onAddToCollections={handleSingleAddToCollections}
                onRemoveFromCollection={handleSingleRemoveFromCollection}
                onShowToast={showToast}
                onImageClick={handleImageClick}
                scrollContainerRef={galleryScrollRef}
              />
            </div>
          </div>
        </div>

        {/* Display Settings Panel */}
        {showDisplaySettings && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/20"
              onClick={() => setShowDisplaySettings(false)}
            />

            {/* Settings Panel */}
            <div className="fixed top-20 right-6 z-50">
              <GalleryDisplaySettingsPanel
                isOpen={showDisplaySettings}
                onClose={() => setShowDisplaySettings(false)}
                settings={displaySettings}
                onSettingsChange={handleDisplaySettingsChange}
                language={selectedLanguage}
              />
            </div>
          </>
        )}

        {/* Trash Confirmation Modal (Bulk) */}
        {showTrashConfirm && (
          <>
            <div
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowTrashConfirm(false)}
            />
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md p-6 bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center">
                  <Icon name={ICONS.TRASH} size={20} className="text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {t.gallery?.confirmTrashTitle || 'Move to Trash?'}
                </h3>
              </div>
              <p className="text-gray-300 mb-2">
                {t.gallery?.confirmTrashMessage || 'Selected items will be moved to trash.'}
              </p>
              <p className="text-sm text-gray-400 mb-6">
                {(t.gallery?.confirmTrashCount || 'Items selected: {count}').replace('{count}', selectedIds.size.toString())}
                {' '}
                {t.gallery?.confirmTrashNote || 'Items will be deleted after 30 days.'}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowTrashConfirm(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  {t.common?.cancel || 'Cancel'}
                </button>
                <button
                  onClick={handleMoveToTrash}
                  disabled={isTrashAction}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isTrashAction ? (t.common?.processing || 'Processing...') : (t.gallery?.moveToTrash || 'Move to Trash')}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Single Item Trash Confirmation Modal */}
        {showSingleTrashConfirm && pendingSingleTrashId && (
          <>
            <div
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setShowSingleTrashConfirm(false);
                setPendingSingleTrashId(null);
              }}
            />
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md p-6 bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center">
                  <Icon name={ICONS.TRASH} size={20} className="text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {t.gallery?.confirmTrashTitle || 'Move to Trash?'}
                </h3>
              </div>
              <p className="text-gray-300 mb-2">
                {t.gallery?.confirmTrashSingleMessage || 'This item will be moved to trash.'}
              </p>
              <p className="text-sm text-gray-400 mb-4">
                {t.gallery?.confirmTrashNote || 'Items will be deleted after 30 days.'}
              </p>

              {/* Don't show again checkbox */}
              <label className="flex items-center gap-2 mb-6 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={skipSingleTrashConfirm}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setSkipSingleTrashConfirm(newValue);
                    localStorage.setItem('gallery_skip_single_trash_confirm', newValue.toString());
                  }}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500 focus:ring-offset-gray-900"
                />
                <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                  {t.gallery?.dontShowAgain || "Don't show this again"}
                </span>
              </label>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowSingleTrashConfirm(false);
                    setPendingSingleTrashId(null);
                  }}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  {t.common?.cancel || 'Cancel'}
                </button>
                <button
                  onClick={() => executeSingleItemTrash(pendingSingleTrashId)}
                  disabled={isTrashAction}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isTrashAction ? (t.common?.processing || 'Processing...') : (t.gallery?.moveToTrash || 'Move to Trash')}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Collection Select Modal */}
        <CollectionSelectModal
          isOpen={showCollectionModal}
          onClose={() => setShowCollectionModal(false)}
          onSubmit={handleAddToCollections}
          collections={collections}
          selectedCollectionIds={[]}
          selectedLanguage={selectedLanguage}
        />

        {/* Gallery Lightbox Modal */}
        <MediaPreviewModal
          isOpen={lightboxIndex !== null}
          slot={lightboxIndex !== null ? allSlots[lightboxIndex] ?? null : null}
          onClose={handleLightboxClose}
          onToggleFavorite={() => {}}
          onMark={handleMark}
          onDraftFinal={handleLightboxDraftFinal}
          onMoveToTrash={handleLightboxTrash}
          onRecreate={handleLightboxRecreate}
          onRemoveFromCollection={handleLightboxRemoveFromCollection}
          collections={collections}
          onAddToCollections={handleLightboxAddToCollections}
          language={selectedLanguage}
          allSlots={allSlots}
          currentSlotIndex={lightboxIndex ?? undefined}
          onSlotNavigate={handleSlotNavigate}
          isLoadingMore={loading && lightboxIndex !== null}
        />
      </div>

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 ${
          toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        } text-white`}>
          {toast.message}
        </div>
      )}
    </MainSidebarLayout>
  );
};

export default GalleryContent;
